import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, mergeMap, Observable, of } from 'rxjs';
import {
  CacheFactoryService,
  ICacheService,
} from '../../services/cache-factory.service';
import { EsriUtils } from '../../utils/esri.utils';
import Portal from '@arcgis/core/portal/Portal';
import PortalItem from '@arcgis/core/portal/PortalItem';
import PopupTemplate from '@arcgis/core/PopupTemplate';

@Injectable()
export class LayersInfoService {
  private cacheService: ICacheService;

  constructor(private http: HttpClient, cacheFactory: CacheFactoryService) {
    this.cacheService = cacheFactory.getOrCreate('layers-info');
  }

  getLayerInfo(url: string) {
    return this.cacheService.getCached({
      path: url,
      source: () => {
        return this.http.get<any>(url, { params: { f: 'json' } });
      },
    });
  }

  getLayerTitle(url: string): Observable<string> {
    return this.getLayerInfo(url).pipe(map((res) => res?.mapName ?? res?.name));
  }

  getLayerGeomType(url: string): Observable<string> {
    return this.getLayerInfo(url).pipe(map((res) => res?.geometryType));
  }

  hasAttachments(url: string): Observable<boolean> {
    return this.getLayerInfo(url).pipe(map((res) => !!res?.hasAttachments));
  }

  getDisplayFieldName(url: string): Observable<string> {
    return this.getDisplayField(url).pipe(map((field) => field?.name));
  }

  getDisplayField(url: string): Observable<__esri.Field> {
    return this.cacheService.getCached({
      path: `${url}@@displayField`,
      source: () => {
        return combineLatest(this.getLayerInfo(url), this.getFields(url)).pipe(
          map(([info, fields]) => {
            const displayFieldName = info?.displayField;

            let displayField = fields.find((x) => x.alias === displayFieldName);

            if (!displayField) {
              displayField = fields.find((x) => x.name === displayFieldName);
            }

            return displayField;
          })
        );
      },
    });
  }

  getObjectIdFieldName(url: string): Observable<string> {
    return this.getFields(url).pipe(
      map((fields) => fields?.find((field) => field.type === 'oid')?.name)
    );
  }

  getFields(url: string): Observable<__esri.Field[]> {
    return this.cacheService.getCached({
      path: `${url}@@fields`,
      source: () => {
        return this.getLayerInfo(url).pipe(
          map((res) => {
            return ((res?.fields ?? []) as any[]).map<__esri.Field>((field) => {
              return EsriUtils.fieldFromJson(field);
            });
          })
        );
      },
    });
  }

  getPopupTemplateFromPortalItem(
    url: string
  ): Observable<__esri.PopupTemplate> {
    return this.cacheService.getCached({
      path: `${url}@@portalItem@@PopupTemplate`,
      source: () => {
        return this.getLayerInfoFromPortalItem(url).pipe(
          map((layerInfo) => {
            const popupInfo = layerInfo?.popupInfo;

            if (popupInfo) {
              return PopupTemplate.fromJSON(popupInfo);
            }

            return null;
          })
        );
      },
    });
  }

  getLayerInfoFromPortalItem(url: string): Observable<any> {
    if (!url) {
      return of(null);
    }

    let id = Number(
      url
        .split('/')
        .filter((x) => !!x)
        .reverse()[0]
    );

    return this.cacheService.getCached({
      path: `${url}@@portalItem@@LayerInfo`,
      source: () => {
        return this.getDataFromPortalItem(url).pipe(
          map((data) => {
            const layerInfo = (data?.layers as any[])?.find((x) => {
              return x?.id === id;
            });

            return layerInfo ?? null;
          })
        );
      },
    });
  }

  getPortalItem(url: string): Observable<__esri.PortalItem> {
    return this.cacheService.getCached({
      path: `${url}@@portalItem`,
      source: () => {
        return combineLatest([
          this.getLayerInfo(url),
          this.getPortal(url),
        ]).pipe(
          mergeMap(([info, portal]) => {
            return this.getPortalItemByItemId(info.serviceItemId, portal);
          })
        );
      },
    });
  }

  getDataFromPortalItem(url: string) {
    return this.cacheService.getCached({
      path: `URL:${url}@@portalItem@@data`,
      source: () => {
        return this.getPortalItem(url).pipe(
          mergeMap((portalItem) => {
            return this.getDataFromPortalItemByItemId(
              portalItem?.id,
              portalItem?.portal
            );
          })
        );
      },
    });
  }

  getDataFromPortalItemByItemId(
    itemId: string,
    portal: __esri.Portal
  ): Observable<any> {
    if (!itemId || !portal) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `${itemId}@@portalItem@@data`,
      source: () => {
        return this.getPortalItemByItemId(itemId, portal).pipe(
          mergeMap((portalItem) => {
            return EsriUtils.fetchDataByPortalItem(portalItem, 'json');
          })
        );
      },
    });
  }

  getPortalItemByItemId(
    itemId: string,
    portal: __esri.Portal
  ): Observable<__esri.PortalItem> {
    if (!itemId || !portal) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `${itemId}@@portalItem`,
      source: () => {
        const portalItem = new PortalItem({
          id: itemId,
          portal,
        });

        return EsriUtils.load(portalItem).pipe(
          map(() => {
            return portalItem;
          }),
          catchError((err) => {
            return of(null);
          })
        );
      },
    });
  }

  getPortal(url: string): Observable<__esri.Portal> {
    if (!url) {
      return of(null);
    }

    let urlInstance = new URL(url);
    urlInstance.search = '';
    urlInstance.pathname = 'portal';
    url = urlInstance.toString();

    return this.cacheService.getCached({
      path: `${url}@@portal`,
      source: () => {
        const portal = new Portal({
          url,
        });

        return EsriUtils.load(portal).pipe(
          map(() => {
            return portal;
          }),
          catchError((err) => {
            return of(null);
          })
        );
      },
    });
  }
}
